import vid from "../../assets/video/artline-homepage-scroll.mp4.asset.json";
import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/artline";

const img = (
  file: string,
  alt: string,
  width: number,
  height: number,
  span: 2 | 3 | 6 = 3,
  small = true,
) => ({
  src: `${dir}/${file}.webp`,
  ...(small ? { src900: `${dir}/${file}-900.webp` } : {}),
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

export const artline: CaseStudy = {
  slug: "artline",
  title: "Artline",
  cardTitle: "Сайт-каталог для производителя заборов Artline",
  subtitle: "Производство вентилируемых заборов и систем ограждений",
  industry: { label: "Производство", slug: "production" },
  h1: "Разработка сайта-каталога для производителя заборов Artline",
  lead:
    "Разработали сайт-каталог для производителя вентилируемых заборов и систем ограждений Artline. Спроектировали каталог комплектаций, показали конструкцию продукта, добавили расчёт стоимости и формы заявок. Подготовили структуру проекта к дальнейшему SEO-продвижению.",

  seo: {
    primaryKeyword: "разработка сайта для производителя заборов",
    secondaryKeywords: [
      "сайт-каталог для производителя ограждений",
      "создание сайта для производственной компании",
      "разработка сайта для производителя",
      "корпоративный сайт для производителя",
      "разработка каталога продукции",
      "сайт для производства систем ограждений",
      "разработка сайта-каталога",
      "сайт для производителя строительных материалов",
    ],
    semanticEntities: [
      "производство",
      "системы ограждений",
      "вентилируемые заборы",
      "ворота",
      "калитки",
      "комплектации",
      "каталог продукции",
      "расчёт стоимости",
      "UX/UI",
      "адаптивная версия",
      "Tilda",
      "SEO-подготовка",
      "формы заявок",
    ],
    excludedKeywords: [
      "купить забор",
      "цена забора",
      "монтаж забора",
      "забор жалюзи",
      "откатные ворота Москва",
      "забор под ключ",
    ],
    title: "Разработка сайта для производителя заборов — кейс Artline",
    description:
      "Кейс HAY.agency: разработка сайта-каталога для производителя систем ограждений Artline. Структура каталога, UX/UI, мобильная версия и SEO-подготовка.",
    h1: "Разработка сайта-каталога для производителя заборов Artline",
    slug: "artline",
    canonicalUrl: `${SITE_URL}/cases/artline`,
    ogTitle: "Разработка сайта для производителя заборов — кейс Artline",
    ogDescription:
      "Сайт-каталог для производителя систем ограждений Artline: структура каталога комплектаций, UX/UI, мобильная версия и SEO-подготовка.",
    ogImage: `${SITE_URL}/og/cases/artline.webp`,
    robotsIndex: true,
    datePublished: "2023-09-01",
    dateModified: "2026-08-28",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов и сайтов-каталогов",
      to: routes.corporate,
      note: "Основная услуга проекта: структура каталога, дизайн и вёрстка",
    },
    relatedIndustry: {
      label: "Разработка сайтов для производственных компаний",
      to: routes.corporate,
      note: "Отраслевое направление: производство и сложные товарные каталоги",
    },
    relatedCases: [
      {
        label: "Интернет-магазин дизайнерской мебели Vosart",
        to: routes.ecom,
        note: "Товарный каталог с подробными карточками и подбором по параметрам",
      },
      {
        label: "Сайт ювелирного производства Godsforge",
        to: routes.ecom,
        note: "Производственный бренд с продажей продукции через сайт",
      },
      {
        label: "Сайт-каталог светового оборудования Styllight",
        to: routes.corporate,
        note: "Технически сложный продукт, разложенный на комплектации и серии",
      },
    ],
    relatedArticles: [
      {
        label: "Как считать себестоимость и маркетинговые расходы производству",
        to: "/blog/cost-accounting",
      },
    ],
  },

  cta: {
    title: "Нужен сайт для производственной компании?",
    text: "Спроектируем структуру каталога, покажем сложный продукт понятно и подготовим страницы к SEO-продвижению.",
    button: "Обсудить сайт",
  },

  client: {
    name: "Artline",
    legalName: "Artline (группа компаний ESG Professional)",
    url: "https://artlinem.ru/",
    city: "Москва и Московская область",
    industry: "Производство металлических ограждений",
  },
  year: "2023",
  services: [
    "Аналитика и структура каталога",
    "UX/UI-дизайн",
    "Разработка на Tilda (Zero Block)",
    "Адаптивная версия",
    "SEO-подготовка структуры и метатегов",
  ],
  platform: "Tilda (Zero Block), адаптивная вёрстка от 320 px",
  tags: [
    { label: "Корпоративные сайты и каталоги", to: routes.corporate },
    { label: "Интернет-магазины и товарные каталоги", to: routes.ecom },
    { label: "SEO-продвижение", to: routes.seo },
    { label: "Геоперформанс", to: routes.geo },
  ],
  facts: [
    { label: "Клиент", value: "Artline — производство систем ограждений" },
    { label: "Отрасль", value: "Производство, благоустройство участка" },
    { label: "География", value: "Москва и Московская область, доставка по России" },
    { label: "Тип проекта", value: "Сайт-каталог продукции" },
    {
      label: "Задача",
      value: "Объяснить конструкцию и комплектации продукта и собрать заявки на замер",
    },
    {
      label: "Что сделали",
      value: "Структура каталога, UX/UI, дизайн, вёрстка, формы, SEO-подготовка",
    },
    { label: "Платформа", value: "Tilda, Zero Block, адаптив от 320 px" },
    { label: "Услуги", value: "Аналитика, дизайн, разработка, SEO-подготовка" },
    { label: "Год запуска", value: "2023" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог из трёх продуктовых линеек — заборы, ворота, калитки — и трёх комплектаций внутри каждой линейки.",
      "Карточки комплектаций «Лайт», «Комфорт» и «Премиум» с составом конструкции и ценой за погонный метр.",
      "Отдельные посадочные страницы под продуктовые направления клиента и формы заявки на замер на каждой из них.",
      "Адаптивная версия от 320 px, в которой сохранены каталог, цены и вызов замерщика.",
    ],
  },

  story: [
    {
      title: "Задача и особенности бизнеса",
      paragraphs: [
        "Artline производит, доставляет и монтирует секционные ограждения типа «жалюзи», ворота и калитки. Покупатели — частные владельцы участков и подрядчики по благоустройству, которые выбирают конструкцию долго и сравнивают предложения по составу работ, срокам и цене.",
        "До редизайна продукт объясняли менеджеры по телефону: клиент не понимал, из чего состоит секция, чем комплектация «Лайт» отличается от «Премиума» и почему стоимость считается за погонный метр. Шаблонный сайт-визитка с одной формой такую задачу не закрывает — он не показывает конструкцию и не даёт сравнить варианты.",
        "Нам нужно было спроектировать каталог, который снимает эти вопросы до первого контакта с менеджером и одновременно готов к продвижению по коммерческим запросам клиента.",
      ],
    },
    {
      title: "Что разработали",
      paragraphs: [
        "Работы шли от аналитики продукта и его комплектаций к прототипу, дизайну и вёрстке на Tilda.",
      ],
      bullets: [
        "Разбор продукта: линейки, комплектации, состав секции, логика ценообразования за погонный метр.",
        "Прототип каталога и карточек комплектаций.",
        "UX/UI-дизайн первых экранов, каталога, карточек и блоков дополнительных услуг.",
        "Вёрстка в Zero Block с адаптивом от 320 px.",
        "Формы заявки: расчёт стоимости, вызов замерщика, обратный звонок, квиз.",
        "Подготовка структуры и метатегов под дальнейшее SEO-продвижение.",
      ],
    },
    {
      title: "Структура и пользовательские сценарии",
      paragraphs: [
        "Навигация построена вокруг продуктовых направлений: заборы, ворота, калитки. Из меню и из слайдера на первом экране пользователь попадает сразу в нужный раздел, а не в общий каталог.",
        "Внутри раздела продукт разложен на три комплектации, которые сравниваются по составу и цене за погонный метр. Дополнительные услуги — фасадное освещение и видеонаблюдение — вынесены в каталог как часть комплексного решения, а не спрятаны в текст.",
        "Точки конверсии расставлены под разную готовность: изучающему продукт — расчёт стоимости в карточке, определившемуся — вызов замерщика, сомневающемуся — обратный звонок и квиз в финале страницы.",
      ],
    },
  ],

  features: [
    {
      title: "Сравнение комплектаций",
      problem:
        "Покупатель не понимал, за что платит: три варианта секции выглядели одинаково и различались только ценой.",
      solution:
        "Сделали карточки «Лайт», «Комфорт» и «Премиум» с перечнем элементов конструкции — столбы, накладки, фундаментная лента, каменный цоколь — и ценой за погонный метр.",
      benefit:
        "Клиент выбирает комплектацию сам и приходит к менеджеру с конкретным вопросом, а не с вопросом «сколько стоит забор».",
    },
    {
      title: "Объяснение конструкции",
      problem: "Забор «жалюзи» — неочевидный продукт: неясно, как устроена секция и зачем нужна вентиляция.",
      solution:
        "Собрали блок с 3D-схемами секции, разбором элементов и фотографиями смонтированных объектов вместо стоковых иллюстраций.",
      benefit: "Снимаются типовые возражения до звонка, разговор с менеджером начинается с расчёта.",
    },
    {
      title: "Расчёт и заявка на замер",
      problem:
        "Стоимость зависит от длины участка и комплектации, поэтому единой цены на сайте быть не может.",
      solution:
        "Разместили в карточке расчёт от погонного метра и короткую форму вызова замерщика, продублированную в каталоге и в финале страницы.",
      benefit: "Заявка оставляется на любом этапе изучения продукта, без обязательного звонка.",
    },
    {
      title: "Дополнительные услуги в каталоге",
      problem: "Освещение и видеонаблюдение продавались отдельно и терялись в переписке с менеджером.",
      solution: "Вынесли их в каталог отдельными карточками рядом с ограждениями.",
      benefit:
        "Ограждение подаётся как комплексное решение по участку, что отличает производителя от продавцов профнастила.",
    },
  ],

  design: {
    title: "Дизайн и визуальная система",
    paragraphs: [
      "Визуальная концепция — «технологичное производство»: тёмная графитовая база, много воздуха и один акцентный зелёный. Такой контраст уводит проект от эстетики строительного рынка и держит внимание на самой конструкции.",
      "Типографика построена на паре Bebas Neue и Arial: акцидентные заголовки задают ритм первых экранов, основной текст остаётся спокойным и читаемым в описаниях комплектаций.",
      "Продукт подаётся собственным контентом — фотосъёмкой смонтированных объектов, 3D-визуализациями секций и раскладкой элементов. Интерфейсные решения подчинены сравнению: одинаковая сетка карточек, единый порядок характеристик, цена и кнопка расчёта всегда на одном месте.",
    ],
  },

  identity: {
    fonts: [
      { name: "Bebas Neue", role: "Заголовки H1–H2", sample: "СИСТЕМЫ ОГРАЖДЕНИЙ" },
      { name: "Arial", role: "Текст и интерфейс", sample: "Комплексный подход к ограждению участка" },
    ],
    colors: [
      { hex: "#242424", name: "Графит" },
      { hex: "#ffffff", name: "Белый" },
      { hex: "#f5f5f5", name: "Светло-серый" },
      { hex: "#7cbc3d", name: "Акцентный зелёный" },
    ],
  },

  cover: img(
    "artline-case-cover",
    "Обложка кейса: сайт-каталог производителя вентилируемых заборов Artline",
    1920,
    1080,
    6,
  ),

  coverVideo: vid.url,
  coverPoster: {
    src: `${dir}/artline-homepage-scroll-poster.webp`,
    src900: `${dir}/artline-homepage-scroll-poster-900.webp`,
    alt: "Прокрутка главной страницы сайта-каталога Artline",
    width: 1920,
    height: 1080,
    span: 6,
    wide: true,
  },

  gallery: [
    img("artline-case-cover", "Обложка кейса Artline с первым экраном сайта-каталога", 1920, 1080, 6),
    img("artline-fence-catalog", "Каталог заборов на сайте Artline", 1920, 1246, 3),
    img("artline-gate-catalog", "Каталог ворот и калиток на сайте Artline", 1920, 1246, 3),
    img("artline-louvered-fence-card", "Карточка забора «жалюзи» с составом конструкции", 1920, 1246, 3),
    img("artline-configuration-calculator", "Калькулятор расчёта стоимости конфигурации ограждения", 1920, 1246, 3),
    img("artline-construction-explained", "Схема конструкции секции ограждения с разбором элементов", 1920, 1246, 6),
    img("artline-completed-projects", "Галерея реализованных объектов производителя ограждений Artline", 1920, 1246, 3),
    img("artline-delivery", "Блок доставки продукции Artline по России", 1920, 1246, 3),
  ],
  responsive: {
    text: [
      "Мобильная версия отвечает за основной сценарий каталога: пользователь листает комплектации, сравнивает состав секции и вызывает замерщика прямо с телефона.",
      "Карточка комплектации на мобильном перестроена в одну колонку: сначала визуал секции, затем состав конструкции, затем цена за погонный метр и кнопка расчёта — без горизонтального скролла и увеличения.",
      "Форма заявки на замер сокращена до имени и телефона, а меню каталога свёрнуто в короткий список продуктовых направлений.",
    ],
    images: [
      img("artline-mobile-catalog", "Мобильная версия: главная страница и каталог комплектаций", 1920, 1700, 2),
      img("artline-mobile-product-gallery", "Мобильная версия: карточка продукта и фотогалерея", 1920, 1700, 2),
      img("artline-mobile-service", "Мобильная версия: раздел доставки и монтажа", 1920, 1700, 2),
    ],
  },
};
